import * as THREE from 'three';
import { createGlowTexture } from '../utils/createGlowTexture.js';
import { WHISPERS } from '../data/whispers.js';

const SKY_RADIUS = 800;
const HOVER_DISCOVER_DELAY_MS = 250; // hold the hover this long to mark as found

// Minimum angular separation (degrees) so two whispers don't overlap into
// an unreadable cluster, and so a whisper doesn't sit on top of an already
// highlighted star (planet, family member, easter egg, etc).
const MIN_WHISPER_SEPARATION_DEG = 10;
const MIN_FROM_HIGHLIGHTED_DEG = 10;
const MAX_PLACEMENT_ATTEMPTS = 250;

// Soft pale glow - dimmer than every other interactive object
const WHISPER_GLOW = createGlowTexture('230, 230, 245');

function azAlt(azDeg, altDeg) {
  const az = (azDeg * Math.PI) / 180;
  const alt = (altDeg * Math.PI) / 180;
  const cosAlt = Math.cos(alt);
  return new THREE.Vector3(
    SKY_RADIUS * cosAlt * Math.sin(az),
    SKY_RADIUS * Math.sin(alt),
    -SKY_RADIUS * cosAlt * Math.cos(az),
  );
}

function angularDistanceDeg(a, b) {
  const denom = a.length() * b.length();
  if (denom === 0) return 180;
  const cos = Math.max(-1, Math.min(1, a.dot(b) / denom));
  return (Math.acos(cos) * 180) / Math.PI;
}

/**
 * WhisperStar - a tiny dim star carrying one fun fact about Megha.
 *
 * Discovered by HOVERING (not clicking). The fact appears as a label at
 * the bottom of the screen. Hovering for at least HOVER_DISCOVER_DELAY_MS
 * marks it as found, so brushing past doesn't accidentally count.
 *
 * Cursor stays as default (not pointer) - these are *not* meant to look
 * clickable. The label appearing is the entire feedback.
 */
class WhisperStar {
  constructor({ position, whisper, onHover, onUnhover, onDiscover }) {
    this.id = `whisper-${whisper.id}`;
    this.discoveryId = `whisper-${whisper.id}`;
    this.category = 'whispers';
    this.interactable = true;
    this.whisper = whisper;
    this._onHoverCb = onHover;
    this._onUnhoverCb = onUnhover;
    this._onDiscoverCb = onDiscover;
    this._discoverTimer = null;

    // Single sprite - visual + click target in one. Same reliable pattern
    // as Sirius. Scale tuned to be findable by accident while still
    // visually subtle (about 22-25 px at typical viewing distance).
    const material = new THREE.SpriteMaterial({
      map: WHISPER_GLOW,
      color: 0xb0b0c8,
      transparent: true,
      opacity: 0.65,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      depthTest: false,
    });
    this.mesh = new THREE.Sprite(material);
    this.mesh.position.copy(position);
    this.mesh.scale.setScalar(20);
    this.mesh.renderOrder = 9;
    this.mesh.userData.star = this;
  }

  onHover() {
    // Don't change cursor - whispers are not clickable
    if (this._onHoverCb) this._onHoverCb(this.whisper);
    if (this._discoverTimer) clearTimeout(this._discoverTimer);
    this._discoverTimer = setTimeout(() => {
      if (this._onDiscoverCb) this._onDiscoverCb(this.whisper);
      this._discoverTimer = null;
    }, HOVER_DISCOVER_DELAY_MS);
  }

  onUnhover() {
    if (this._discoverTimer) {
      clearTimeout(this._discoverTimer);
      this._discoverTimer = null;
    }
    if (this._onUnhoverCb) this._onUnhoverCb();
  }

  onClick() {
    // Whispers don't open cards. Click does nothing.
  }

  cardHTML() {
    return null;
  }
}

/**
 * Create whisper stars at random positions across the sky (one per whisper).
 * Returns a flat array - caller adds each to the sky.
 *
 * `avoidPositions` is an optional array of Vector3s (highlighted stars,
 * planets, etc) to keep whispers away from. Placement uses rejection
 * sampling so whispers don't bunch into unreadable clusters and don't
 * land on top of already-marked objects.
 */
export function createWhisperStars({ onHover, onUnhover, onDiscover, avoidPositions = [] }) {
  const stars = [];
  const placed = [];

  for (const whisper of WHISPERS) {
    let position = null;
    let lastCandidate = null;

    for (let attempt = 0; attempt < MAX_PLACEMENT_ATTEMPTS; attempt++) {
      const az = Math.random() * 360;
      const alt = -5 + Math.random() * 80; // -5 to 75 degrees
      const candidate = azAlt(az, alt);
      lastCandidate = candidate;

      let ok = true;
      for (const p of placed) {
        if (angularDistanceDeg(candidate, p) < MIN_WHISPER_SEPARATION_DEG) {
          ok = false;
          break;
        }
      }
      if (ok) {
        for (const p of avoidPositions) {
          if (angularDistanceDeg(candidate, p) < MIN_FROM_HIGHLIGHTED_DEG) {
            ok = false;
            break;
          }
        }
      }
      if (ok) {
        position = candidate;
        break;
      }
    }

    // Degrade gracefully: if rejection sampling can't find a spot, accept
    // the last candidate rather than dropping the whisper entirely.
    if (!position) position = lastCandidate;
    placed.push(position);

    stars.push(new WhisperStar({
      position,
      whisper,
      onHover,
      onUnhover,
      onDiscover,
    }));
  }
  return stars;
}
